import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  EditOutlined as EditIcon,
  DownloadOutlined as DownloadIcon,
  MailOutline as MailIcon,
  ScienceOutlined as ScienceIcon,
  HistoryOutlined as HistoryIcon,
  CheckCircleOutline,
  Close as CloseIcon,
  PrintOutlined as PrintIcon,
} from "@mui/icons-material";
import { labReportService, referralService } from "../../services/lis";
import { revokeExternalShare } from "../../services/messages";

// Microscope SVG Icon matching design header
function MicroscopeLogo({ size = 44 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 36 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M20.5 4.5L16.2 8.8L19.7 12.3L24 8L20.5 4.5Z"
        fill="#0067BC"
      />
      <path
        d="M14.8 10.2L9.5 15.5C8.8 16.2 8.8 17.3 9.5 18L10.5 19L17.5 12L16.5 11C16 10.5 15.3 10.2 14.8 10.2Z"
        fill="#00A3A3"
      />
      <path
        d="M26.5 14C23.5 14 21 16.5 21 19.5C21 21.2 21.8 22.8 23.1 23.8L21 27H29L26.9 23.8C28.2 22.8 29 21.2 29 19.5C29 16.5 26.5 14 26.5 14Z"
        fill="#0067BC"
      />
      <path
        d="M7 29H29V32H7V29Z"
        fill="#0067BC"
      />
      <path
        d="M13 25H23V27H13V25Z"
        fill="#00A3A3"
      />
    </svg>
  );
}

export default function LabReportView({
  isDark,
  resultId,
  onBack,
  onEdit,
  isModal = false,
  fullScreen = false,
  compact = false,
  readOnly = false,
}) {
  const reportPrintRef = useRef(null);

  // Theme tokens
  const cardBg = isDark ? "#1E1E1E" : "#FFFFFF";
  const innerSectionBg = isDark ? "#141414" : "#FFFFFF";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#E2E8F0";
  const textPrimary = isDark ? "#F8FAFC" : "#0F172A";
  const textSecondary = isDark ? "#94A3B8" : "#64748B";
  const primaryBlue = "#2563EB";

  // Compact layout: active whenever in modal (both normal AND fullscreen) or if compact prop is set
  const isCompact = isModal || compact;

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Email Share Dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [newShareId, setNewShareId] = useState(null);
  const [revokedShares, setRevokedShares] = useState([]);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);

  // Version History Dialog
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState([]);

  // PACS Sharing State
  const [sendingToPacs, setSendingToPacs] = useState(false);
  const [pacsSuccessMessage, setPacsSuccessMessage] = useState("");

  const handleSendToPacs = async () => {
    if (!report?._id) return;
    setSendingToPacs(true);
    setPacsSuccessMessage("");
    try {
      await referralService.sendReport({
        labResultId: report.labResultId || resultId,
        labOrderId: report.labOrderId || report.labOrder?._id,
        referralId: report.referralId || report.labOrder?.referralId || null,
      });
      setPacsSuccessMessage("Report and diagnostic notification successfully sent to PACS / Radiologist!");
      setTimeout(() => setPacsSuccessMessage(""), 5000);
    } catch (err) {
      console.error("Failed to send report to PACS:", err);
      setError(err?.response?.data?.error || "Failed to send report to PACS");
    } finally {
      setSendingToPacs(false);
    }
  };

  // Fetch report data
  const fetchReport = useCallback(async () => {
    if (!resultId) {
      setReport(null);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await labReportService.getForResult(resultId);
      if (res?.data?.data) {
        const d = res.data.data;
        setReport({
          _id: d._id,
          version: d.version || 1,
          emailDeliveries: d.emailDeliveries || [],
          referralId: d.referralId || d.labOrder?.referralId || null,
          labOrderId: d.labOrder?._id || d.labOrder || null,
          labResultId: d.labResult?._id || d.labResult || resultId,
          hasPacsReferral: !!(
            d.referralId ||
            d.labOrder?.referralId ||
            d.studyUID ||
            (d.patientInfo?.referringDoctor &&
              d.patientInfo.referringDoctor !== "—" &&
              !d.patientInfo.referringDoctor.toLowerCase().includes("walk-in"))
          ),
          patientInfo: {
            patientName:
              d.patientInfo?.patientName ||
              (d.patient?.firstName
                ? `${d.patient.firstName} ${d.patient.lastName || ""}`
                : "Patient"),
            patientId:
              d.patientInfo?.patientId ||
              d.patientInfo?.mrn ||
              d.patient?.mrn ||
              "—",
            gender: d.patientInfo?.gender || d.patientInfo?.sex || d.patient?.sex || "—",
            age: d.patientInfo?.age || "—",
            referringDoctor:
              d.patientInfo?.referringDoctor ||
              d.sampleInfo?.referringDoctor ||
              "—",
          },
          testInfo: {
            testName: d.testInfo?.testName || "Laboratory Test",
            sampleCondition: d.testInfo?.sampleCondition || "Adequate",
            sampleType: d.testInfo?.sampleType || d.sampleInfo?.specimenType || "Blood",
            sampleId: d.testInfo?.sampleId || d.specimen?.sampleId || d.sampleInfo?.sampleId || "—",
            containerType: d.testInfo?.containerType || d.specimen?.containerType || "",
            bodySite: d.testInfo?.bodySite || d.specimen?.bodySite || "",
            laterality: d.testInfo?.laterality || d.specimen?.laterality || "",
            bodySiteDetail: d.testInfo?.bodySiteDetail || d.specimen?.bodySiteDetail || "",
            collectedAt: d.testInfo?.collectionTimestamp || d.specimen?.collectionTimestamp || d.collectedAt || null,
          },
          results:
            d.results && d.results.length > 0
              ? d.results.map((r) => ({
                  parameterName: r.parameter || r.parameterName || r.testName || "—",
                  value:
                    r.result !== undefined && r.result !== null && r.result !== ""
                      ? r.result
                      : r.value !== undefined && r.value !== null && r.value !== ""
                      ? r.value
                      : "—",
                  unit: r.unit || "",
                  referenceRangeText: r.referenceRange || r.referenceRangeText || "—",
                  interpretation:
                    r.interpretation || (r.flag === "NORMAL" || !r.flag ? "Normal" : r.flag),
                }))
              : [],
          interpretationText:
            d.interpretationText || "Results completed.",
          reportedBy: {
            name: d.reportedBy?.name || "Laboratory Scientist",
            role: d.reportedBy?.role || "Laboratory Scientist",
            dateTime: d.createdAt
              ? new Date(d.createdAt).toLocaleString("en-US", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "—",
          },
          history: d.versionHistory || [],
          clinicalRequirements: d.clinicalRequirements || d.labOrder?.clinicalRequirements || d.order?.clinicalRequirements || null,
          raw: d,
        });
      } else {
        setReport(null);
      }
    } catch (err) {
      setReport(null);
      setError(err?.response?.data?.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [resultId]);

  useEffect(() => {
    setNewShareId(null);
    setRevokedShares([]);
    fetchReport();
  }, [fetchReport]);

  // Download PDF
  const handleDownload = () => {
    window.print();
  };

  // Send Email
  const handleSendEmail = async () => {
    if (!emailTo || !report?._id) return;
    setEmailSending(true);
    try {
      const response = await labReportService.email(report._id, {
        recipientEmail: emailTo,
      });
      setNewShareId(response.data.shareId);
      setEmailSending(false);
      setEmailSuccess(true);
      setTimeout(() => {
        setEmailDialogOpen(false);
        setEmailSuccess(false);
        setEmailTo("");
      }, 1200);
    } catch (err) {
      setEmailSending(false);
      setEmailSuccess(false);
      setError(err.response?.data?.message || "Failed to send access link");
    }
  };

  // Show Versions
  const handleShowVersions = async () => {
    if (!report?._id) return;
    try {
      const res = await labReportService.getVersions(report._id);
      if (res?.data?.data && res.data.data.length > 0) {
        setVersions(res.data.data);
      } else {
        setVersions(report.history || []);
      }
      setVersionsOpen(true);
    } catch (err) {
      setVersions(report.history || []);
      setVersionsOpen(true);
    }
  };

  if (loading) {
    return (
      <Box sx={{ width: "100%", py: 12, display: "flex", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress size={36} sx={{ color: "#2563EB" }} />
      </Box>
    );
  }

  if (!report) {
    return (
      <Box sx={{ width: "100%", pb: 5 }}>
        <Button
          onClick={onBack}
          startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
          sx={{
            textTransform: "none",
            fontFamily: "'Satoshi', sans-serif",
            fontWeight: 700,
            fontSize: "0.875rem",
            color: "#2563EB",
            p: 0,
            mb: 3,
            "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
          }}
        >
          Back to Results
        </Button>
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: "14px",
            bgcolor: cardBg,
            border: `1px solid ${borderColor}`,
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: "1.05rem",
              fontWeight: 600,
              color: textSecondary,
              mb: 2.5,
            }}
          >
            {error || "No report selected. Please select a completed result to view its report."}
          </Typography>
          <Button
            variant="contained"
            onClick={onBack}
            sx={{
              bgcolor: "#2563EB",
              textTransform: "none",
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: 700,
              borderRadius: "8px",
              px: 3,
            }}
          >
            Back to Results
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", pb: 3 }}>
      {/* 1. Top Navigation Bar (Back Link + Action Buttons) */}
      <Stack
        direction="row"
        justifyContent={isModal ? "flex-end" : "space-between"}
        alignItems="center"
        sx={{
          mb: isCompact ? 1 : 2,
          maxWidth: isModal ? "680px" : "100%",
          mx: isModal ? "auto" : undefined,
        }}
      >
        {/* Back Link */}
        {!isModal && (
          <Button
            onClick={onBack}
            startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
            sx={{
              textTransform: "none",
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: 700,
              fontSize: "0.875rem",
              color: "#2563EB",
              p: 0,
              "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
            }}
          >
            Back to Results
          </Button>
        )}

        {/* Top Action Buttons */}
        <Stack direction="row" spacing={isCompact ? 1 : 1.5} alignItems="center">
          {/* Send to PACS (Only for Laboratory personnel in LIS, never for Radiologists or read-only views) */}
          {!readOnly && !isModal && report.hasPacsReferral && (
            <Button
              variant="contained"
              onClick={handleSendToPacs}
              disabled={sendingToPacs}
              startIcon={sendingToPacs ? <CircularProgress size={15} color="inherit" /> : <ScienceIcon sx={{ fontSize: 17 }} />}
              sx={{
                borderRadius: "8px",
                bgcolor: "#7C3AED",
                color: "#FFFFFF",
                textTransform: "none",
                fontFamily: "'Satoshi', sans-serif",
                fontWeight: 700,
                fontSize: "0.82rem",
                px: 2,
                py: 0.7,
                boxShadow: "0 2px 10px rgba(124, 58, 237, 0.3)",
                "&:hover": {
                  bgcolor: "#6D28D9",
                },
              }}
            >
              {sendingToPacs ? "Sending to PACS..." : "Send to PACS"}
            </Button>
          )}

          {/* Edit Report (Only for Laboratory personnel in LIS, never for Radiologists) */}
          {!readOnly && onEdit && (
            <Button
              variant="outlined"
              onClick={() => onEdit?.(report._id)}
              startIcon={<EditIcon sx={{ fontSize: 17 }} />}
              sx={{
                borderRadius: "8px",
                borderColor: "#3B82F6",
                color: "#2563EB",
                textTransform: "none",
                fontFamily: "'Satoshi', sans-serif",
                fontWeight: 600,
                fontSize: isCompact ? "0.78rem" : "0.82rem",
                px: isCompact ? 1.5 : 2,
                py: isCompact ? 0.4 : 0.7,
                height: isCompact ? 30 : "auto",
                "&:hover": {
                  borderColor: "#1D4ED8",
                  bgcolor: isDark ? "rgba(37,99,235,0.1)" : "#EFF6FF",
                },
              }}
            >
              Edit Report
            </Button>
          )}

          {/* Download PDF */}
          <Button
            variant="outlined"
            onClick={handleDownload}
            startIcon={<DownloadIcon sx={{ fontSize: isCompact ? 15 : 17 }} />}
            sx={{
              borderRadius: "8px",
              borderColor: "#3B82F6",
              color: "#2563EB",
              textTransform: "none",
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: 600,
              fontSize: isCompact ? "0.78rem" : "0.82rem",
              px: isCompact ? 1.5 : 2,
              py: isCompact ? 0.4 : 0.7,
              height: isCompact ? 30 : "auto",
              "&:hover": {
                borderColor: "#1D4ED8",
                bgcolor: isDark ? "rgba(37,99,235,0.1)" : "#EFF6FF",
              },
            }}
          >
            Download PDF
          </Button>

          {/* Print */}
          <Button
            variant="outlined"
            onClick={() => window.print()}
            startIcon={<PrintIcon sx={{ fontSize: isCompact ? 15 : 17 }} />}
            sx={{
              borderRadius: "8px",
              borderColor: "#3B82F6",
              color: "#2563EB",
              textTransform: "none",
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: 600,
              fontSize: isCompact ? "0.78rem" : "0.82rem",
              px: isCompact ? 1.5 : 2,
              py: isCompact ? 0.4 : 0.7,
              height: isCompact ? 30 : "auto",
              "&:hover": {
                borderColor: "#1D4ED8",
                bgcolor: isDark ? "rgba(37,99,235,0.1)" : "#EFF6FF",
              },
            }}
          >
            Print
          </Button>

          {/* Share via Email */}
          <Button
            variant="outlined"
            onClick={() => { setError(''); setEmailDialogOpen(true); }}
            startIcon={<MailIcon sx={{ fontSize: isCompact ? 15 : 17 }} />}
            sx={{
              borderRadius: "8px",
              borderColor: "#3B82F6",
              color: "#2563EB",
              textTransform: "none",
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: 600,
              fontSize: isCompact ? "0.78rem" : "0.82rem",
              px: isCompact ? 1.5 : 2,
              py: isCompact ? 0.4 : 0.7,
              height: isCompact ? 30 : "auto",
              "&:hover": {
                borderColor: "#1D4ED8",
                bgcolor: isDark ? "rgba(37,99,235,0.1)" : "#EFF6FF",
              },
            }}
          >
            Share via Email
          </Button>

          {/* Version History Tooltip Button */}
          <Tooltip title="View Version History & Audit Trail">
            <IconButton
              size="small"
              onClick={handleShowVersions}
              sx={{ color: textSecondary, ml: 0.5, p: isCompact ? "4px" : "8px" }}
            >
              <HistoryIcon sx={{ fontSize: isCompact ? 18 : 20 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {pacsSuccessMessage && (
        <Alert
          severity="success"
          sx={{
            mb: 3,
            borderRadius: "12px",
            fontFamily: "'Satoshi', sans-serif",
            fontWeight: 600,
            fontSize: "0.85rem",
          }}
          onClose={() => setPacsSuccessMessage("")}
        >
          {pacsSuccessMessage}
        </Alert>
      )}

      {/* 2. Main Diagnostic Lab Report Document */}
      <Paper
        ref={reportPrintRef}
        elevation={0}
        sx={{
          borderRadius: isCompact ? "10px" : "16px",
          bgcolor: cardBg,
          border: `1px solid ${borderColor}`,
          boxShadow: isDark
            ? "0 4px 20px rgba(0,0,0,0.4)"
            : "0 4px 20px rgba(15, 23, 42, 0.04)",
          p: isCompact
            ? { xs: 1.25, sm: 1.5, md: 1.75 }
            : { xs: 2.5, sm: 3.5, md: 4 },
          maxWidth: isModal ? "680px" : fullScreen ? "1000px" : "100%",
          mx: isModal || fullScreen ? "auto" : undefined,
        }}
      >
        {/* Document Header (Brand & Logo) */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: isCompact ? 1.25 : 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: isCompact ? 1 : 2 }}>
            <MicroscopeLogo size={isCompact ? 30 : 46} />
            <Box>
              <Typography
                sx={{
                  fontFamily: "'Satoshi', sans-serif",
                  fontSize: isCompact ? "1.2rem" : "1.75rem",
                  fontWeight: 800,
                  lineHeight: 1.1,
                  letterSpacing: "-0.02em",
                }}
              >
                <Box component="span" sx={{ color: "#0067BC" }}>
                  Dyco
                </Box>
                <Box component="span" sx={{ color: "#00A3A3" }}>
                  Vue
                </Box>
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Satoshi', sans-serif",
                  fontSize: isCompact ? "0.64rem" : "0.75rem",
                  fontWeight: 800,
                  color: isDark ? "#CBD5E1" : "#1E293B",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  mt: isCompact ? 0.15 : 0.4,
                }}
              >
                DIAGNOSTICS LABORATORY
              </Typography>
            </Box>
          </Box>

          <Box sx={{ textAlign: "right", display: { xs: "none", sm: "block" } }}>
            <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.68rem" : "0.75rem", fontWeight: 700, color: textSecondary }}>
              CLINICAL PATHOLOGY REPORT
            </Typography>
            <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.64rem" : "0.72rem", color: textSecondary, opacity: 0.8 }}>
              Electronic Diagnostic Record
            </Typography>
          </Box>
        </Box>

        {/* COMPACT SIDE-BY-SIDE PATIENT & SPECIMEN INFORMATION */}
        <Grid container spacing={isCompact ? 1 : 2.5} sx={{ mb: isCompact ? 1.25 : 3.5 }}>
          {/* Card 1: PATIENT INFORMATION */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: isCompact ? 1 : 2.5,
                borderRadius: "8px",
                border: `1px solid ${borderColor}`,
                bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#F8FAFC",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Satoshi', sans-serif",
                  fontSize: isCompact ? "0.7rem" : "0.8rem",
                  fontWeight: 800,
                  color: "#2563EB",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  mb: isCompact ? 0.6 : 2,
                }}
              >
                PATIENT INFORMATION
              </Typography>

              <Grid container spacing={isCompact ? 0.4 : 1.5}>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.72rem" : "0.82rem", color: textSecondary, width: isCompact ? 76 : 95, flexShrink: 0 }}>
                      Patient Name
                    </Typography>
                    <Typography sx={{ color: textSecondary, fontSize: isCompact ? "0.72rem" : "inherit" }}>:</Typography>
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.76rem" : "0.85rem", fontWeight: 700, color: textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {report.patientInfo.patientName}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.72rem" : "0.82rem", color: textSecondary, width: isCompact ? 76 : 95, flexShrink: 0 }}>
                      Referring Doctor
                    </Typography>
                    <Typography sx={{ color: textSecondary, fontSize: isCompact ? "0.72rem" : "inherit" }}>:</Typography>
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.76rem" : "0.85rem", fontWeight: 700, color: textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {report.patientInfo.referringDoctor || "Dr. Adebayo"}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.72rem" : "0.82rem", color: textSecondary, width: isCompact ? 76 : 95, flexShrink: 0 }}>
                      Patient ID
                    </Typography>
                    <Typography sx={{ color: textSecondary, fontSize: isCompact ? "0.72rem" : "inherit" }}>:</Typography>
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.76rem" : "0.85rem", fontWeight: 700, color: textPrimary }}>
                      {report.patientInfo.patientId || "—"}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.72rem" : "0.82rem", color: textSecondary, width: isCompact ? 40 : 50, flexShrink: 0 }}>
                      Gender
                    </Typography>
                    <Typography sx={{ color: textSecondary, fontSize: isCompact ? "0.72rem" : "inherit" }}>:</Typography>
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.76rem" : "0.85rem", fontWeight: 600, color: textPrimary }}>
                      {report.patientInfo.gender || "—"}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.72rem" : "0.82rem", color: textSecondary, width: isCompact ? 24 : 32, flexShrink: 0 }}>
                      Age
                    </Typography>
                    <Typography sx={{ color: textSecondary, fontSize: isCompact ? "0.72rem" : "inherit" }}>:</Typography>
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.76rem" : "0.85rem", fontWeight: 600, color: textPrimary }}>
                      {report.patientInfo.age || "—"}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Card 2: TEST & SPECIMEN INFORMATION */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: isCompact ? 1 : 2.5,
                borderRadius: "8px",
                border: `1px solid ${borderColor}`,
                bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#F8FAFC",
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Satoshi', sans-serif",
                  fontSize: isCompact ? "0.7rem" : "0.8rem",
                  fontWeight: 800,
                  color: "#2563EB",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  mb: isCompact ? 0.6 : 2,
                }}
              >
                TEST & SPECIMEN INFORMATION
              </Typography>

              <Grid container spacing={isCompact ? 0.4 : 1.5}>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.72rem" : "0.82rem", color: textSecondary, width: isCompact ? 76 : 95, flexShrink: 0 }}>
                      Test / Profile
                    </Typography>
                    <Typography sx={{ color: textSecondary, fontSize: isCompact ? "0.72rem" : "inherit" }}>:</Typography>
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.76rem" : "0.85rem", fontWeight: 700, color: textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {report.testInfo.testName}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.72rem" : "0.82rem", color: textSecondary, width: isCompact ? 76 : 95, flexShrink: 0 }}>
                      Sample ID
                    </Typography>
                    <Typography sx={{ color: textSecondary, fontSize: isCompact ? "0.72rem" : "inherit" }}>:</Typography>
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.76rem" : "0.85rem", fontWeight: 700, color: "#2563EB" }}>
                      {report.testInfo.sampleId || "—"}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.72rem" : "0.82rem", color: textSecondary, width: isCompact ? 76 : 95, flexShrink: 0 }}>
                      Sample Type
                    </Typography>
                    <Typography sx={{ color: textSecondary, fontSize: isCompact ? "0.72rem" : "inherit" }}>:</Typography>
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.74rem" : "0.82rem", fontWeight: 600, color: textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {report.testInfo.sampleType || "Blood"}
                      {report.testInfo.containerType && report.testInfo.containerType !== "N/A"
                        ? ` • ${report.testInfo.containerType}`
                        : ""}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.72rem" : "0.82rem", color: textSecondary, width: isCompact ? 76 : 95, flexShrink: 0 }}>
                      Condition
                    </Typography>
                    <Typography sx={{ color: textSecondary, fontSize: isCompact ? "0.72rem" : "inherit" }}>:</Typography>
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.74rem" : "0.82rem", fontWeight: 600, color: textPrimary }}>
                      {report.testInfo.sampleCondition || "Adequate"}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.72rem" : "0.82rem", color: textSecondary, width: isCompact ? 76 : 95, flexShrink: 0 }}>
                      Anatomical Site
                    </Typography>
                    <Typography sx={{ color: textSecondary, fontSize: isCompact ? "0.72rem" : "inherit" }}>:</Typography>
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.74rem" : "0.82rem", fontWeight: 700, color: textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {[
                        report.testInfo.laterality,
                        report.testInfo.bodySite,
                        report.testInfo.bodySiteDetail ? `(${report.testInfo.bodySiteDetail})` : "",
                      ]
                        .filter(Boolean)
                        .join(" ") || "—"}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.72rem" : "0.82rem", color: textSecondary, width: isCompact ? 76 : 95, flexShrink: 0 }}>
                      Collected At
                    </Typography>
                    <Typography sx={{ color: textSecondary, fontSize: isCompact ? "0.72rem" : "inherit" }}>:</Typography>
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: isCompact ? "0.74rem" : "0.82rem", fontWeight: 600, color: textPrimary }}>
                      {report.testInfo.collectedAt
                        ? new Date(report.testInfo.collectedAt).toLocaleString("en-US", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "—"}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>

        {/* SECTION 2.5: CLINICAL PATHOLOGY & SPECIMEN REQUIREMENTS (if present) */}
        {report.clinicalRequirements && (
          <Box
            sx={{
              mb: isCompact ? 2 : 3.5,
              p: isCompact ? 1.5 : 2.5,
              borderRadius: "10px",
              border: `1px solid ${borderColor}`,
              bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#F8FAFC",
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Satoshi', sans-serif",
                fontSize: isCompact ? "0.75rem" : "0.8rem",
                fontWeight: 800,
                color: "#2563EB",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                mb: isCompact ? 1 : 1.5,
              }}
            >
              CLINICAL ORDERING DETAILS & SPECIMEN SPECIFICATIONS
            </Typography>

            <Grid container spacing={1.5}>
              {report.clinicalRequirements.category && (
                <Grid item xs={12} sm={6} md={4}>
                  <Typography sx={{ fontSize: "0.75rem", color: textSecondary, fontWeight: 600 }}>
                    Category
                  </Typography>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: textPrimary }}>
                    {report.clinicalRequirements.category.replace(/_/g, " ")}
                  </Typography>
                </Grid>
              )}
              {report.clinicalRequirements.orderingPhysician && (
                <Grid item xs={12} sm={6} md={4}>
                  <Typography sx={{ fontSize: "0.75rem", color: textSecondary, fontWeight: 600 }}>
                    Ordering Physician / Pathologist
                  </Typography>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: textPrimary }}>
                    {report.clinicalRequirements.orderingPhysician}
                  </Typography>
                </Grid>
              )}
              {report.clinicalRequirements.surgicalBiopsy?.anatomicSite && (
                <Grid item xs={12} sm={6} md={4}>
                  <Typography sx={{ fontSize: "0.75rem", color: textSecondary, fontWeight: 600 }}>
                    Anatomic Site & Procedure
                  </Typography>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: textPrimary }}>
                    {report.clinicalRequirements.surgicalBiopsy.anatomicSite} — {report.clinicalRequirements.surgicalBiopsy.procedurePerformed}
                  </Typography>
                </Grid>
              )}
              {report.clinicalRequirements.surgicalBiopsy?.clinicalDiagnosisIndication && (
                <Grid item xs={12} sm={6} md={4}>
                  <Typography sx={{ fontSize: "0.75rem", color: textSecondary, fontWeight: 600 }}>
                    Clinical Diagnosis / Indication
                  </Typography>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: textPrimary }}>
                    {report.clinicalRequirements.surgicalBiopsy.clinicalDiagnosisIndication}
                  </Typography>
                </Grid>
              )}
              {report.clinicalRequirements.gynCytology?.specimenSource && (
                <Grid item xs={12} sm={6} md={4}>
                  <Typography sx={{ fontSize: "0.75rem", color: textSecondary, fontWeight: 600 }}>
                    Cytology Specimen Source
                  </Typography>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: textPrimary }}>
                    {report.clinicalRequirements.gynCytology.specimenSource} ({report.clinicalRequirements.gynCytology.collectionDevice})
                  </Typography>
                </Grid>
              )}
              {report.clinicalRequirements.frozenSection?.intraoperativeUrgency && (
                <Grid item xs={12} sm={6} md={4}>
                  <Typography sx={{ fontSize: "0.75rem", color: "#DC2626", fontWeight: 700 }}>
                    Intraoperative Urgency
                  </Typography>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 800, color: "#DC2626" }}>
                    {report.clinicalRequirements.frozenSection.intraoperativeUrgency} (OR: {report.clinicalRequirements.frozenSection.operatingRoomLocation})
                  </Typography>
                </Grid>
              )}
            </Grid>

            {/* Multiple Specimen Containers */}
            {report.clinicalRequirements.specimens && report.clinicalRequirements.specimens.length > 0 && (
              <Box sx={{ mt: 2, pt: 1.5, borderTop: `1px dashed ${borderColor}` }}>
                <Typography sx={{ fontSize: "0.75rem", color: textSecondary, fontWeight: 700, mb: 1 }}>
                  Specimen Containers ({report.clinicalRequirements.specimens.length})
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {report.clinicalRequirements.specimens.map((sp, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        p: 1.2,
                        borderRadius: "8px",
                        bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#FFFFFF",
                        border: `1px solid ${borderColor}`,
                        fontSize: "0.78rem",
                      }}
                    >
                      <Typography sx={{ fontWeight: 700, color: "#2563EB" }}>
                        {sp.containerLabel || `Specimen ${idx + 1}`}
                      </Typography>
                      <Typography sx={{ color: textPrimary, fontSize: "0.75rem" }}>
                        {sp.anatomicSite ? `${sp.anatomicSite} • ` : ""}{sp.fixative || "Formalin"} {sp.volume ? `(${sp.volume})` : ""}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        )}

        {/* SECTION 3: RESULTS TABLE */}
        <Box sx={{ mb: isCompact ? 1.5 : 4 }}>
          <Typography
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: isCompact ? "0.75rem" : "0.85rem",
              fontWeight: 800,
              color: "#2563EB",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              mb: isCompact ? 0.75 : 2,
            }}
          >
            RESULTS
          </Typography>

          <TableContainer
            sx={{
              borderRadius: isCompact ? "8px" : "10px",
              border: `1px solid ${borderColor}`,
              overflow: "hidden",
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: isDark ? "#171717" : "#F8FAFC" }}>
                  {[
                    "Parameter",
                    "Result",
                    "Unit",
                    "Reference Range",
                    "Interpretation",
                  ].map((header) => (
                    <TableCell
                      key={header}
                      sx={{
                        fontFamily: "'Satoshi', sans-serif",
                        fontWeight: 700,
                        fontSize: isCompact ? "0.66rem" : "0.75rem",
                        color: textSecondary,
                        py: isCompact ? 0.5 : 1.6,
                        px: isCompact ? 1 : 2.5,
                        borderBottom: `1px solid ${borderColor}`,
                      }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {report.results.map((row, idx) => (
                  <TableRow
                    key={idx}
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      "&:hover": {
                        bgcolor: isDark
                          ? "rgba(255,255,255,0.02)"
                          : "rgba(241,245,249,0.5)",
                      },
                    }}
                  >
                    {/* Parameter */}
                    <TableCell
                      sx={{
                        fontFamily: "'Satoshi', sans-serif",
                        fontWeight: 600,
                        fontSize: isCompact ? "0.74rem" : "0.85rem",
                        color: textPrimary,
                        px: isCompact ? 1 : 2.5,
                        py: isCompact ? 0.45 : 1.5,
                        borderBottom: `1px solid ${borderColor}`,
                      }}
                    >
                      {row.parameterName}
                    </TableCell>

                    {/* Result */}
                    <TableCell
                      sx={{
                        fontFamily: "'Satoshi', sans-serif",
                        fontWeight: 700,
                        fontSize: isCompact ? "0.74rem" : "0.85rem",
                        color: textPrimary,
                        px: isCompact ? 1 : 2.5,
                        py: isCompact ? 0.45 : 1.5,
                        borderBottom: `1px solid ${borderColor}`,
                      }}
                    >
                      {row.value}
                    </TableCell>

                    {/* Unit */}
                    <TableCell
                      sx={{
                        fontFamily: "'Satoshi', sans-serif",
                        fontSize: isCompact ? "0.72rem" : "0.82rem",
                        color: textSecondary,
                        px: isCompact ? 1 : 2.5,
                        py: isCompact ? 0.45 : 1.5,
                        borderBottom: `1px solid ${borderColor}`,
                      }}
                    >
                      {row.unit || "—"}
                    </TableCell>

                    {/* Reference Range */}
                    <TableCell
                      sx={{
                        fontFamily: "'Satoshi', sans-serif",
                        fontSize: isCompact ? "0.72rem" : "0.82rem",
                        color: textSecondary,
                        px: isCompact ? 1 : 2.5,
                        py: isCompact ? 0.45 : 1.5,
                        borderBottom: `1px solid ${borderColor}`,
                      }}
                    >
                      {row.referenceRangeText || "—"}
                    </TableCell>

                    {/* Interpretation */}
                    <TableCell
                      sx={{
                        fontFamily: "'Satoshi', sans-serif",
                        fontWeight: 600,
                        fontSize: isCompact ? "0.74rem" : "0.85rem",
                        color:
                          row.interpretation === "Normal"
                            ? "#16A34A"
                            : row.interpretation === "High"
                            ? "#EA580C"
                            : "#2563EB",
                        px: isCompact ? 1 : 2.5,
                        py: isCompact ? 0.45 : 1.5,
                        borderBottom: `1px solid ${borderColor}`,
                      }}
                    >
                      {row.interpretation}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* SECTION 4: INTERPRETATION */}
        <Box sx={{ mb: isCompact ? 1.5 : 4 }}>
          <Typography
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: isCompact ? "0.75rem" : "0.85rem",
              fontWeight: 800,
              color: "#16A34A",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              mb: isCompact ? 0.5 : 1.5,
            }}
          >
            INTERPRETATION
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: isCompact ? "0.78rem" : "0.875rem",
              color: textPrimary,
              lineHeight: 1.45,
            }}
          >
            {report.interpretationText}
          </Typography>
        </Box>

        {/* SECTION 5: REPORTED BY */}
        <Box sx={{ mt: isCompact ? 1.5 : 2 }}>
          <Typography
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontSize: isCompact ? "0.8rem" : "0.85rem",
              fontWeight: 800,
              color: "#2563EB",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              mb: isCompact ? 1 : 2,
            }}
          >
            REPORTED BY
          </Typography>

          <Stack direction="row" spacing={isCompact ? 3 : 6} alignItems="center">
            {/* Scientist Profile */}
            <Stack direction="row" spacing={isCompact ? 1.25 : 1.8} alignItems="center">
              <Box
                sx={{
                  width: isCompact ? 36 : 44,
                  height: isCompact ? 36 : 44,
                  borderRadius: "50%",
                  bgcolor: isDark ? "rgba(37, 99, 235, 0.2)" : "#EFF6FF",
                  color: "#2563EB",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid #DBEAFE",
                }}
              >
                <ScienceIcon sx={{ fontSize: isCompact ? 18 : 22 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "'Satoshi', sans-serif",
                    fontWeight: 700,
                    fontSize: isCompact ? "0.88rem" : "0.95rem",
                    color: textPrimary,
                    lineHeight: 1.2,
                  }}
                >
                  {report.reportedBy.name}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Satoshi', sans-serif",
                    fontSize: isCompact ? "0.74rem" : "0.78rem",
                    color: textSecondary,
                    mt: 0.2,
                  }}
                >
                  {report.reportedBy.role}
                </Typography>
              </Box>
            </Stack>

            {/* Date & Time */}
            <Box>
              <Typography
                sx={{
                  fontFamily: "'Satoshi', sans-serif",
                  fontSize: isCompact ? "0.7rem" : "0.74rem",
                  color: textSecondary,
                  fontWeight: 500,
                }}
              >
                Date & Time
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Satoshi', sans-serif",
                  fontSize: isCompact ? "0.82rem" : "0.875rem",
                  fontWeight: 700,
                  color: textPrimary,
                  mt: 0.2,
                }}
              >
                {report.reportedBy.dateTime}
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Paper>

      {/* Share via Email Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: "95vw", sm: "480px" },
            maxWidth: "500px !important",
            maxHeight: "85vh",
            bgcolor: cardBg,
            backgroundImage: "none",
            borderRadius: "14px",
            border: `1px solid ${borderColor}`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 1.25,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${borderColor}`,
            fontFamily: "'Satoshi', sans-serif",
            fontWeight: 700,
            color: textPrimary,
            fontSize: "1.05rem",
          }}
        >
          Share Laboratory Report via Email
          <IconButton size="small" onClick={() => setEmailDialogOpen(false)} sx={{ color: textSecondary }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 1.75, overflowY: "auto" }}>
            {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          {emailSuccess && (
            <Alert severity="success" sx={{ mb: 1.5, borderRadius: "8px" }}>
              Access link sent. The recipient must verify their email to download the report.
            </Alert>
          )}
          <Stack spacing={1.5} sx={{ mt: 0.25 }}>
            <TextField
              label="Recipient Email"
              placeholder="doctor@hospital.com or patient@mail.com"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              fullWidth
              size="small"
            />
              <Alert severity="info">Only the latest finalized report can be shared. The link expires in 24 hours and requires an email code. Amending the report withdraws access to this version.</Alert>
              {[...new Set([newShareId, ...(report.emailDeliveries || []).map(delivery => delivery.externalShare)].filter(Boolean))].map(shareId => (
                <Button key={shareId} disabled={emailSending || revokedShares.includes(shareId)} onClick={async () => {
                  setEmailSending(true);
                  try {
                    await revokeExternalShare(shareId);
                    setRevokedShares(current => [...current, shareId]);
                  } catch (err) { setError("Unable to revoke this link. Only its sender or an administrator can revoke it."); }
                  finally { setEmailSending(false); }
                }}>{revokedShares.includes(shareId) ? 'Access revoked' : `Revoke access link ${shareId.slice(-6)}`}</Button>
              ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${borderColor}`, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            onClick={() => setEmailDialogOpen(false)}
            sx={{ color: textSecondary, textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSendEmail}
            disabled={!emailTo || emailSending}
            startIcon={!emailSending && <MailIcon sx={{ fontSize: 17 }} />}
            sx={{
              bgcolor: "#2563EB",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#1D4ED8" },
            }}
          >
            {emailSending ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : (
              "Send Report"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version History & Audit Dialog */}
      <Dialog
        open={versionsOpen}
        onClose={() => setVersionsOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: "95vw", sm: "520px" },
            maxWidth: "540px !important",
            maxHeight: "85vh",
            bgcolor: cardBg,
            backgroundImage: "none",
            borderRadius: "14px",
            border: `1px solid ${borderColor}`,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 1.25,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${borderColor}`,
            fontFamily: "'Satoshi', sans-serif",
            fontWeight: 700,
            color: textPrimary,
            fontSize: "1.05rem",
          }}
        >
          Report Version History & Audit Trail
          <IconButton size="small" onClick={() => setVersionsOpen(false)} sx={{ color: textSecondary }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2.5, py: 1.75, overflowY: "auto" }}>
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: textSecondary,
              mb: 1.5,
              fontFamily: "'Satoshi', sans-serif",
            }}
          >
            All report amendments and versions are permanently saved for compliance.
          </Typography>

          {versions.map((v, i) => (
            <Paper
              key={i}
              elevation={0}
              sx={{
                p: 1.5,
                mb: 1,
                borderRadius: "8px",
                bgcolor:
                  i === 0
                    ? isDark
                      ? "rgba(37, 99, 235, 0.1)"
                      : "#EFF6FF"
                    : isDark
                    ? "#161616"
                    : "#F8FAFC",
                border: `1px solid ${
                  i === 0 ? "rgba(37, 99, 235, 0.3)" : borderColor
                }`,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      color: textPrimary,
                    }}
                  >
                    Version {v.version || i + 1}{" "}
                    {i === 0 && (
                      <Chip
                        label="Current Active"
                        size="small"
                        sx={{
                          ml: 1,
                          bgcolor: "#2563EB",
                          color: "#fff",
                          height: 18,
                          fontSize: "0.65rem",
                          fontWeight: 700,
                        }}
                      />
                    )}
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: textSecondary, mt: 0.25 }}>
                    {v.reportedBy?.name || v.reportedByName || (typeof v.reportedBy === 'string' ? v.reportedBy : "Laboratory Scientist")} •{" "}
                    {v.createdAt
                      ? new Date(v.createdAt).toLocaleString()
                      : "—"}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.25, borderTop: `1px solid ${borderColor}`, display: "flex", justifyContent: "flex-end" }}>
          <Button
            onClick={() => setVersionsOpen(false)}
            sx={{ color: textSecondary, textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
